import React, { useEffect, useMemo, useRef } from 'react';
import "./styles/detectionLabel.css";
import { useLocation } from 'react-router-dom';
import useDetectionLabelHook from "./hooks/useDetectionLabelHook.js"
import useLabelingStore from './store/useDetectionStore.js';
import useModelManagementStore from '../../modelManagement/store/useModelManagementStore';
import DetectionFilter from './components/DetectionFilter';
import DetectionLeft from './components/DetectionLeft';
import DetectionRight from './components/DetectionRight';
import DetectionMiddleTop from './components/DetectionMiddleTop';
import DetectionMiddle from './components/DetectionMiddle';
import useModeHandlers from './hooks/useModeHandlers';
import useStrokeWidthUpdater from './hooks/useStrokeWidthUpdater';
import useImageLoader from './hooks/useImageLoader';
import useBoundingBoxDrawing from './hooks/useBoundingBoxDrawing';
import usePolygonDrawing from './hooks/usePolygonDrawing';
import useObjectModifications from './hooks/useObjectModifications';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import useObjectSelection from './hooks/useObjectSelection';
import useUndoRedo from './hooks/useUndoRedo';
import useAutoLabel from './hooks/useAutoLabel';
import useModelPreLabel from './hooks/useModelPreLabel';
import AutoLabelPanel from './components/AutoLabelPanel';
import { modelIdOf } from '../../../../utils/labelingTarget';

const MODEL_STORAGE_KEY = 'lf-detection-model';
const VERSION_STORAGE_KEY = 'lf-detection-version-id';

const DetectionLabel = () => {

  const { handlers } = useDetectionLabelHook();
  const { handleGetOneDetectionData } = handlers;
  const location = useLocation();
  const { setModelInfo, modelInfo } = useModelManagementStore();

  const {
    canvas,
    images,
    currentImageIndex,
    currentClassId,
    // selectedAnnIndex,
    startX,
    startY,
    rect,
    zoomLevel,
    copiedBoxes,
    // error,
    mode,
    polygonPoints,
    polygonObj,
    isDrawingPolygon,
    classes,
    BASE_STROKE_WIDTH,
    isCrosshairEnabled,

    // labelingMode,
    setCurrentClassId,
    setSelectedAnnIndex,
    setStartX,
    setStartY,
    setRect,
    setCopiedBoxes,
    setError,

    // setMode,
    setPolygonPoints,
    setPolygonObj,
    setIsDrawingPolygon,
    getCurrentImage,
    updateAnnotations,
    updatePolygons,
    nextImage,
    prevImage,
    resetAll
  } = useLabelingStore();

  const { saveState, undo, redo, canUndo, canRedo } = useUndoRedo();
  const autoLabel = useAutoLabel(saveState);
  // pre-labeling from a version the user already trained (model-in-the-loop)
  const preLabel = useModelPreLabel(saveState);

  // const canvasRef = useCanvasInitialization(setCanvas);
  useModeHandlers(canvas, mode, polygonObj, setIsDrawingPolygon, setPolygonPoints, setPolygonObj);
  useStrokeWidthUpdater(canvas, images, zoomLevel, BASE_STROKE_WIDTH);
  useImageLoader(images, currentImageIndex, canvas, setIsDrawingPolygon, setPolygonPoints, setPolygonObj, setError, classes, BASE_STROKE_WIDTH);
  useBoundingBoxDrawing(canvas, mode, rect, startX, startY, currentClassId, zoomLevel, getCurrentImage, updateAnnotations, setStartX, setStartY, setRect, classes, BASE_STROKE_WIDTH, isCrosshairEnabled, saveState);
  usePolygonDrawing(canvas, mode, polygonPoints, polygonObj, isDrawingPolygon, currentClassId, zoomLevel, getCurrentImage, updatePolygons, setPolygonPoints, setPolygonObj, setIsDrawingPolygon, classes, BASE_STROKE_WIDTH, saveState);
  useObjectModifications(canvas, images, currentImageIndex, updateAnnotations, updatePolygons, getCurrentImage, saveState);
  useKeyboardShortcuts(saveState, undo, redo);
  useObjectSelection(canvas, setSelectedAnnIndex, setCurrentClassId);

  const selectedModelFromState = location.state?.model;
  const versionIdFromState = location.state?.versionId;

  useEffect(() => {
    if (selectedModelFromState) {
      try {
        sessionStorage.setItem(MODEL_STORAGE_KEY, JSON.stringify(selectedModelFromState));
      } catch (err) {
        console.warn('Unable to persist model info', err);
      }
    }
    if (typeof versionIdFromState !== 'undefined') {
      try {
        if (versionIdFromState) {
          sessionStorage.setItem(VERSION_STORAGE_KEY, versionIdFromState);
        } else {
          sessionStorage.removeItem(VERSION_STORAGE_KEY);
        }
      } catch (err) {
        console.warn('Unable to persist version info', err);
      }
    }
  }, [selectedModelFromState, versionIdFromState]);

  const selectedModel = useMemo(() => {
    if (selectedModelFromState) return selectedModelFromState;
    try {
      const stored = sessionStorage.getItem(MODEL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.warn('Unable to read persisted model info', err);
      return null;
    }
  }, [selectedModelFromState]);

  const versionId = useMemo(() => {
    if (versionIdFromState) return versionIdFromState;
    try {
      const stored = sessionStorage.getItem(VERSION_STORAGE_KEY);
      return stored || null;
    } catch (err) {
      console.warn('Unable to read persisted version info', err);
      return null;
    }
  }, [versionIdFromState]);

  const previousModelIdRef = useRef(null);

  useEffect(() => {
    if (
      selectedModel &&
      (!modelInfo || modelIdOf(selectedModel) !== modelIdOf(modelInfo))
    ) {
      setModelInfo(selectedModel);
    }

    // Either shape: a raw Mongo doc carries _id, a flattened one model_id.
    // Reading only model_id made every raw doc look like "no model" and
    // wiped the version that had just loaded.
    const currentId = modelIdOf(selectedModel);
    if (!currentId) {
      resetAll();
      previousModelIdRef.current = null;
      return;
    }

    if (previousModelIdRef.current && previousModelIdRef.current !== currentId) {
      resetAll();
    }
    previousModelIdRef.current = currentId;
  }, [selectedModel, modelInfo, setModelInfo, resetAll]);

  useEffect(() => {
    if (versionId) {
      console.log("Editing model version ID:", versionId);
      const param = { "model_info_id": versionId }
      handleGetOneDetectionData(param)
    }
    else {
      resetAll();
    }
  }, [versionId, handleGetOneDetectionData, resetAll]);

  return (
    <div className="container-fluid detection-label lf-fill-viewport">
      <div className="detection-filter-wrap">
        <DetectionFilter />
      </div>
      <div className="main-content">
        <div className="left-col">
          <DetectionLeft />
        </div>
        <div className="center-col">
          <div className="content-top">
            <DetectionMiddleTop undo={undo} redo={redo} canUndo={canUndo} canRedo={canRedo} />
          </div>
          <div className="content-bottom">
            {autoLabel.autoMode && (
              <AutoLabelPanel
                autoSettings={autoLabel.autoSettings}
                setAutoSettings={autoLabel.setAutoSettings}
                setAutoMode={autoLabel.setAutoMode}
                isDetecting={autoLabel.isDetecting}
                isStopping={autoLabel.isStopping}
                batchProgress={autoLabel.batchProgress}
                detectCurrentImage={autoLabel.detectCurrentImage}
                autoLabelAllUnlabeled={autoLabel.autoLabelAllUnlabeled}
                autoLabelFromMaster={autoLabel.autoLabelFromMaster}
                setCurrentAsMaster={autoLabel.setCurrentAsMaster}
                clearMaster={autoLabel.clearMaster}
                masterImageId={autoLabel.masterImageId}
                stopAll={autoLabel.stopAll}
                preLabel={preLabel}
              />
            )}
            <DetectionMiddle />
          </div>
        </div>
        <div className="right-col">
          <DetectionRight />
        </div>
      </div>
    </div>
  )
};

export default DetectionLabel;